import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Property } from '@/types/property';
import { usePropertyMutations } from '@/hooks/usePropertyMutations';
import { useState, useEffect } from 'react';

interface PropertyEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  property: Property | null;
}

const PropertyEntryModal = ({ isOpen, onClose, property }: PropertyEntryModalProps) => {
  const { updateProperty } = usePropertyMutations();
  const [description, setDescription] = useState(property?.entryDescription || '');
  const [videoUrl, setVideoUrl] = useState(property?.entryVideoUrl || '');
  const [code, setCode] = useState(property?.securityCode || '');
  const [isSaving, setIsSaving] = useState(false);

  // Update local state when property changes
  useEffect(() => {
    if (property) {
      setDescription(property.entryDescription || '');
      setVideoUrl(property.entryVideoUrl || '');
      setCode(property.securityCode || '');
    }
  }, [property]);

  const handleSave = async () => {
    if (!property) return;

    setIsSaving(true);
    try {
      await updateProperty.mutateAsync({
        id: property.id,
        entryDescription: description,
        entryVideoUrl: videoUrl,
        securityCode: code,
      });
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setDescription(property?.entryDescription || '');
    setVideoUrl(property?.entryVideoUrl || '');
    setCode(property?.securityCode || '');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="text-2xl">Instrucciones de Entrada</DialogTitle>
          <p className="text-sm text-muted-foreground">{property?.shortName || property?.title}</p>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="description">Descripción</Label>
            <Textarea
              id="description"
              placeholder="Ej: El ingreso es autónomo con caja de seguridad"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="videoUrl">URL Video</Label>
            <Input
              id="videoUrl"
              type="url"
              placeholder="https://youtube.com/..."
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              URL de YouTube con instrucciones de cómo abrir la puerta
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="code">Código</Label>
            <Input
              id="code"
              placeholder="Código de la caja de seguridad"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PropertyEntryModal;
